import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetBrain, GetBrainId } from "../api/KnowledgeApiCall";
import { KnowledgeCard } from "../api/responsePayload/KnowledgeResponse";
import { showConsoleError } from "../util/ConsoleMessage";
import { KnowledgeType } from "../util/KnowledgeType";
import { resetPagination, setPagination } from "./pagination.slice";
import { AppThunk } from "./common/store";
import { fetchSopById, fetchSopKnowledge } from "./sop.slice";
import { fetchIntents, fetchIntentById } from "./intent.slice";
/* eslint-disable complexity */

interface BrainState {
  knowledgeType: KnowledgeType;
  list: KnowledgeCard[];
  loading: boolean;
  error?: string;
}

const initialBrainState: BrainState = {
  knowledgeType: KnowledgeType.DOCUMENT,
  list: [],
  loading: false,
};

export const fetchBrainList = createAsyncThunk(
  "brain/fetchList",
  async (
    args:
      | { endpoint?: string; id?: number; page?: number; searchQuery?: string }
      | undefined,
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const knowledgeType: KnowledgeType =
        state.brain?.knowledgeType ?? KnowledgeType.DOCUMENT;
      const res = await GetBrain(
        args?.endpoint,
        knowledgeType,
        args?.id,
        args?.page,
        args?.searchQuery
      );
      if (res) {
        dispatch(
          setPagination({
            currentPage: res.current_page,
            nextPageUrl: res.next,
            prevPageUrl: res.previous,
            totalPages: res.total_pages,
            totalCount: res.count,
          })
        );
        return res.data as KnowledgeCard[];
      }
      return [] as KnowledgeCard[];
    } catch (e: any) {
      showConsoleError("fetchBrainList error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const fetchBrainById = createAsyncThunk(
  "brain/fetchById",
  async (id: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const knowledgeType: KnowledgeType =
        state.brain?.knowledgeType ?? KnowledgeType.DOCUMENT;

      const res = await GetBrainId(id, knowledgeType);
      dispatch(resetPagination());

      return res ? (res as KnowledgeCard[]) : [];
    } catch (e: any) {
      showConsoleError("fetchBrainById error", e);
      return rejectWithValue(String(e));
    }
  }
);

type Args = {
  query?: string;
  searchType?: "id" | "query";
  page?: number;
};

export const performBrainSearch =
  ({ query, searchType = "query", page }: Args): AppThunk =>
  (dispatch, getState) => {
    const { brain } = getState();
    const kt = brain.knowledgeType;

    // No query -> just list current tab
    if (!query || query.trim() === "") {
      if (kt === KnowledgeType.SOP)
        return dispatch(fetchSopKnowledge({ page }));
      if (kt === KnowledgeType.INTENT) return dispatch(fetchIntents({ page }));
      return dispatch(fetchBrainList({ page }));
    }

    // ID search
    if (searchType === "id" && Number(query)) {
      const id = Number(query);
      if (kt === KnowledgeType.SOP) return dispatch(fetchSopById(id));
      if (kt === KnowledgeType.INTENT) return dispatch(fetchIntentById(id));
      return dispatch(fetchBrainById(id)); // DOCUMENT; does resetPagination internally
    }

    // Text search
    if (kt === KnowledgeType.SOP) {
      return dispatch(fetchSopKnowledge({ queryParams: { search: query } }));
    }
    if (kt === KnowledgeType.INTENT) {
      return dispatch(fetchIntents({ queryParams: { search: query } }));
    }
    return dispatch(fetchBrainList({ searchQuery: query })); // DOCUMENT
  };

export const brainSlice = createSlice({
  name: "brain",
  initialState: initialBrainState,
  reducers: {
    setKnowledgeType(state, action: PayloadAction<KnowledgeType>) {
      state.knowledgeType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrainList.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchBrainList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchBrainList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBrainById.fulfilled, (state, action) => {
        state.list = action.payload;
      });
  },
});

export const { setKnowledgeType } = brainSlice.actions;
export default brainSlice.reducer;
